import { Button, Input, Label } from '@design-system';
import {
  useMonitorCarriers,
  useMonitorProducts,
  useMonitorStates,
} from '@/features/illustration-monitor/hooks/use-illustration-monitor';
import { MonitorPaymentOption, MonitorProduct } from '@/features/illustration-monitor/types';

export type FilterDraft = {
  insuranceCarrierId: number | null;
  product: MonitorProduct | null;
  clientState: string | null;
  ratedState: string | null;
  primaryAge: number | null;
  primaryGender: string | null;
  primaryTobaccoStatus: string | null;
  paymentOption: MonitorPaymentOption | null;
};

const STATE_DELIMITER = '|';
const encodeStateOption = (clientState: string, ratedState: string) =>
  `${clientState}${STATE_DELIMITER}${ratedState}`;
const decodeStateOption = (encoded: string): { clientState: string; ratedState: string } | null => {
  const [clientState, ratedState] = encoded.split(STATE_DELIMITER);
  if (!clientState || !ratedState) return null;
  return { clientState, ratedState };
};

interface FiltersProps {
  value: FilterDraft;
  onChange: (next: FilterDraft) => void;
  onApply: () => void;
  onClear: () => void;
  canApply: boolean;
}

const GENDERS = ['male', 'female'];
const TOBACCO = ['non-tobacco', 'tobacco'];
const INSURANCE_PAYMENT_OPTIONS: MonitorPaymentOption[] = ['Single Pay', '10-Pay'];
const TRADITIONAL_LTC_PAYMENT_OPTIONS: MonitorPaymentOption[] = ['Annually', 'Monthly'];

const selectClass =
  'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-50 disabled:text-gray-400';

export function IllustrationMonitorFiltersPanel({
  value,
  onChange,
  onApply,
  onClear,
  canApply,
}: FiltersProps) {
  const carriersQuery = useMonitorCarriers();
  const productsQuery = useMonitorProducts(value.insuranceCarrierId);
  const statesQuery = useMonitorStates(
    value.insuranceCarrierId,
    value.product?.variants ?? null,
    value.product?.quoteType ?? null,
  );

  const carriers = carriersQuery.data?.carriers ?? [];
  const products = productsQuery.data?.products ?? [];
  const states = statesQuery.data?.states ?? [];

  const handleProductChange = (displayName: string) => {
    const selected = products.find(p => p.displayName === displayName) ?? null;
    onChange({
      ...value,
      product: selected,
      clientState: null,
      ratedState: null,
      paymentOption: null,
    });
  };

  const handleStateChange = (encoded: string) => {
    const decoded = decodeStateOption(encoded);
    onChange({
      ...value,
      clientState: decoded?.clientState ?? null,
      ratedState: decoded?.ratedState ?? null,
    });
  };

  const paymentOptions = value.product
    ? value.product.quoteType === 'traditionalLTC'
      ? TRADITIONAL_LTC_PAYMENT_OPTIONS
      : value.product.hasFivePayQuotes
        ? ['Single Pay', '5-Pay', '10-Pay']
        : INSURANCE_PAYMENT_OPTIONS
    : [];

  return (
    <aside className="w-64 shrink-0 overflow-y-auto border-r border-gray-200">
      <div className="px-5 pb-6 pt-8">
        <h3 className="mb-5 text-sm font-semibold uppercase tracking-wide text-gray-500">
          Filters
        </h3>

        <div className="space-y-5">
          <Field label="Carrier">
            <select
              className={selectClass}
              value={value.insuranceCarrierId ?? ''}
              onChange={e =>
                onChange({
                  ...value,
                  insuranceCarrierId: e.target.value ? Number(e.target.value) : null,
                  product: null,
                  clientState: null,
                  ratedState: null,
                  paymentOption: null,
                })
              }
              disabled={carriersQuery.isLoading}
            >
              <option value="">Select carrier</option>
              {carriers.map(c => (
                <option key={c.insuranceCarrierId} value={c.insuranceCarrierId}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Product" disabled={!value.insuranceCarrierId}>
            <select
              className={selectClass}
              value={value.product?.displayName ?? ''}
              onChange={e => handleProductChange(e.target.value)}
              disabled={!value.insuranceCarrierId || productsQuery.isLoading}
            >
              <option value="">
                {productsQuery.isLoading ? 'Loading...' : 'Select product'}
              </option>
              {products.map(p => (
                <option key={`${p.displayName}::${p.quoteType}`} value={p.displayName}>
                  {p.displayName}
                  {p.quoteType === 'traditionalLTC' ? ' (Traditional LTC)' : ''}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Client state" disabled={!value.product}>
            <select
              className={selectClass}
              value={
                value.clientState && value.ratedState
                  ? encodeStateOption(value.clientState, value.ratedState)
                  : ''
              }
              onChange={e => handleStateChange(e.target.value)}
              disabled={!value.product || statesQuery.isLoading}
            >
              <option value="">
                {statesQuery.isLoading ? 'Loading...' : 'Select state'}
              </option>
              {states.map(s => (
                <option
                  key={encodeStateOption(s.clientState, s.ratedState)}
                  value={encodeStateOption(s.clientState, s.ratedState)}
                >
                  {s.clientState} (rated: {s.ratedState})
                </option>
              ))}
            </select>
          </Field>

          <Field label="Age" disabled={!value.clientState}>
            <Input
              type="number"
              min={30}
              max={85}
              placeholder="e.g. 55"
              value={value.primaryAge ?? ''}
              onChange={e =>
                onChange({ ...value, primaryAge: e.target.value ? Number(e.target.value) : null })
              }
              disabled={!value.clientState}
            />
          </Field>

          <Field label="Gender" disabled={!value.primaryAge}>
            <select
              className={selectClass}
              value={value.primaryGender ?? ''}
              onChange={e => onChange({ ...value, primaryGender: e.target.value || null })}
              disabled={!value.primaryAge}
            >
              <option value="">Select gender</option>
              {GENDERS.map(g => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Tobacco" disabled={!value.primaryGender}>
            <select
              className={selectClass}
              value={value.primaryTobaccoStatus ?? ''}
              onChange={e => onChange({ ...value, primaryTobaccoStatus: e.target.value || null })}
              disabled={!value.primaryGender}
            >
              <option value="">Any</option>
              {TOBACCO.map(t => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Payment option" disabled={!value.product}>
            <select
              className={selectClass}
              value={value.paymentOption ?? ''}
              onChange={e =>
                onChange({
                  ...value,
                  paymentOption: (e.target.value || null) as MonitorPaymentOption | null,
                })
              }
              disabled={!value.product}
            >
              <option value="">Any</option>
              {paymentOptions.map(p => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="mt-6 flex gap-2">
          <Button
            onClick={onApply}
            disabled={!canApply}
            className="flex-1 bg-primary hover:bg-primary-hover"
          >
            Load quotes
          </Button>
          <Button variant="outline" onClick={onClear} className="flex-1">
            Reset
          </Button>
        </div>
      </div>
    </aside>
  );
}

function Field({
  label,
  disabled,
  children,
}: {
  label: string;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={disabled ? 'opacity-40' : ''}>
      <Label className="mb-1.5 block text-xs font-medium text-gray-600">{label}</Label>
      {children}
    </div>
  );
}
